import * as uuid from 'uuid'
import {
  GenericBlock,
  BlockMessage,
  BubbleOf,
  ButtonBlock,
  ColumnBlock,
  DropdownBlock,
  ImageBlock,
  IntegrationMessage,
  IntegrationMessagePayloadByType,
  RowOf,
  TextBlock,
} from '../types'

type BlockMessagePayload = {
  payload: GenericBlock
  disableInput?: boolean
}

export function integrationMessageToBlockMessage(message: IntegrationMessage): BlockMessage {
  const { metadata, conversationId, userId } = message
  const { payload, disableInput } = integrationMessagePayloadToBlockPayload(message.payload)

  return {
    id: message.id,
    conversationId: conversationId,
    authorId: userId,
    timestamp: new Date(message.createdAt),
    block: payload,
    feedback: message.feedback?.value,
    disableInput,
    metadata,
  }
}

export const integrationMessagePayloadToBlockPayload = (
  message: IntegrationMessage['payload']
): BlockMessagePayload => {
  switch (message.type) {
    case 'audio':
      return audioAdapter(message)
    case 'card':
      return cardAdapter(message)
    case 'carousel':
      return carouselAdapter(message)
    case 'choice':
      return choiceAdapter(message)
    case 'dropdown':
      return dropdownAdapter(message)
    case 'file':
      return fileAdapter(message)
    case 'image':
      return imageAdapter(message)
    case 'location':
      return locationAdapter(message)
    case 'markdown':
      return markdownAdapter(message)
    case 'text':
      return textAdapter(message)
    case 'video':
      return videoAdapter(message)
    case 'bloc':
      return blocAdapter(message)
    case 'custom':
      return { payload: message }

    default: {
      const exhaustiveCheck: never = message
      throw new Error(`Unsuported message type ${exhaustiveCheck}`)
    }
  }
}

type WithBubble<T extends GenericBlock> = BubbleOf<T>

const withBubble = <T extends GenericBlock>(block: T): WithBubble<T> => {
  return {
    block,
    type: 'bubble',
  }
}

const audioAdapter = (x: IntegrationMessagePayloadByType['audio']): BlockMessagePayload => {
  return {
    payload: { type: 'audio', url: x.audioUrl },
  }
}

const cardAdapter = (x: IntegrationMessagePayloadByType['card']): BlockMessagePayload => {
  return {
    payload: withBubble(cardToColumn(x)),
  }
}

const carouselAdapter = (x: IntegrationMessagePayloadByType['carousel']): BlockMessagePayload => {
  return {
    payload: {
      type: 'carousel',
      blocks: x.items.map((i) => cardToColumn({ type: 'card', ...i })),
    },
  }
}

const choiceAdapter = (x: IntegrationMessagePayloadByType['choice']): BlockMessagePayload => {
  const choices = x.options.map((option) => ({ title: option.label, value: option.value }))
  const text = x.text

  const groupId = uuid.v4()

  const textBlock = withBubble({
    type: 'text',
    text,
  })

  if (choices.length === 0) {
    return {
      disableInput: x.disableFreeText,
      payload: textBlock,
    }
  }

  return {
    disableInput: x.disableFreeText,
    payload: {
      type: 'column',
      blocks: [
        textBlock,
        {
          type: 'row',
          blocks: choices.map(({ title, value }) => ({
            type: 'button',
            variant: 'action',
            text: title,
            buttonValue: value,
            groupId,
          })),
        },
      ],
    },
  }
}

const dropdownAdapter = (x: IntegrationMessagePayloadByType['dropdown']) => {
  const options = x.options.map((c) => ({ label: c.label, value: c.value }))
  const text = x.text
  const blocks: (DropdownBlock | TextBlock)[] = [
    {
      type: 'dropdown',
      label: text ?? 'Select an option',
      options,
    },
  ]

  if (text) {
    blocks.unshift({
      type: 'text',
      text,
    })
  }

  return {
    disableInput: true,
    payload: withBubble({
      type: 'column',
      blocks,
    }),
  }
}

const fileAdapter = (x: IntegrationMessagePayloadByType['file']): BlockMessagePayload => {
  return {
    payload: {
      type: 'file',
      url: x.fileUrl,
      title: x.title,
    },
  }
}

const imageAdapter = (x: IntegrationMessagePayloadByType['image']): BlockMessagePayload => {
  return {
    payload: { type: 'image', url: x.imageUrl },
  }
}

const locationAdapter = (x: IntegrationMessagePayloadByType['location']): BlockMessagePayload => {
  return {
    payload: {
      type: 'location',
      latitude: x.latitude,
      longitude: x.longitude,
      title: x.title ?? x.address ?? 'View on map',
    },
  }
}

const markdownAdapter = (x: IntegrationMessagePayloadByType['markdown']): BlockMessagePayload => {
  // TODO: previous implementation did not use `markdown: true` option, should we add something?
  return {
    payload: withBubble({ type: 'text', text: x.markdown }),
  }
}

const textAdapter = (x: IntegrationMessagePayloadByType['text']): BlockMessagePayload => {
  return {
    payload: withBubble({ type: 'text', text: x.text, value: x.value }),
  }
}

const videoAdapter = (x: IntegrationMessagePayloadByType['video']): BlockMessagePayload => {
  return {
    payload: { type: 'video', url: x.videoUrl },
  }
}

const blocAdapter = (x: IntegrationMessagePayloadByType['bloc']) => {
  const output: ColumnBlock = {
    type: 'column',
    blocks: x.items.map((item) => {
      switch (item.type) {
        case 'audio':
          return audioAdapter({ ...item.payload, type: item.type }).payload
        case 'file':
          return fileAdapter({ ...item.payload, type: item.type }).payload
        case 'image':
          return imageAdapter({ ...item.payload, type: item.type }).payload
        case 'location':
          return locationAdapter({ ...item.payload, type: item.type }).payload
        case 'markdown':
          return markdownAdapter({ ...item.payload, type: item.type }).payload
        case 'text':
          return textAdapter({ ...item.payload, type: item.type }).payload
        case 'video':
          return videoAdapter({ ...item.payload, type: item.type }).payload
        default:
          throw new Error('Unsuported message type')
      }
    }),
  }
  return { payload: output }
}

const cardToColumn = (x: IntegrationMessagePayloadByType['card']): ColumnBlock => {
  const title = x.title
  const subtitle = x.subtitle
  const image = x.imageUrl
  const actions: ButtonBlock[] = x.actions.map((a) => {
    switch (a.action) {
      case 'postback':
        return {
          type: 'button',
          variant: 'action',
          text: a.label,
          buttonValue: a.value,
          reusable: true,
        }
      case 'url':
        return {
          type: 'button',
          variant: 'link',
          text: a.label,
          buttonValue: a.value,
          reusable: true,
        }
      case 'say':
        return {
          type: 'button',
          variant: 'action',
          text: a.label,
          buttonValue: a.value,
          reusable: true,
        }
      default:
        throw new Error(`Unknown action type ${a.action}`)
    }
  })

  const cardBlocks: (ImageBlock | TextBlock | RowOf<ButtonBlock>)[] = []

  if (image) {
    cardBlocks.push({
      type: 'image',
      url: image,
    })
  }

  if (title) {
    cardBlocks.push({
      type: 'text',
      text: `#### ${title}`,
    })
  }

  if (subtitle) {
    cardBlocks.push({
      type: 'text',
      text: subtitle,
    })
  }

  if (actions.length > 0) {
    cardBlocks.push({
      type: 'row',
      blocks: actions,
    })
  }

  return {
    type: 'column',
    horizontalAlignment: 'center',
    blocks: [...cardBlocks],
  }
}
